import { useState, useRef, useCallback, useEffect } from 'react'
import type { Lang } from './useLang'

const AUDIO_PATHS: Record<Lang, string> = {
  fr: '/audio/welcome-fr.mp3',
  en: '/audio/welcome-en.mp3',
}

// Fichiers générés par scripts/generate-tts.mjs (Azure TTS)
// Remettre à false si les assets sont absents en local
const AUDIO_AVAILABLE = true

export function useAudio(lang: Lang) {
  const [enabled, setEnabled] = useState(true)
  const [playing, setPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const hasPlayedRef = useRef(false)

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setPlaying(false)
  }, [])

  const play = useCallback(() => {
    if (!AUDIO_AVAILABLE || !enabled) return
    stop()
    const audio = new Audio(AUDIO_PATHS[lang])
    audioRef.current = audio
    audio.onplay = () => setPlaying(true)
    audio.onended = () => setPlaying(false)
    audio.onerror = () => setPlaying(false)
    void audio.play().catch(() => setPlaying(false))
  }, [lang, enabled, stop])

  // Lecture automatique à la première visite
  useEffect(() => {
    if (hasPlayedRef.current) return
    hasPlayedRef.current = true
    const timer = setTimeout(play, 1200)
    return () => clearTimeout(timer)
  }, [play])

  // Changer de langue relance l'audio si activé
  useEffect(() => {
    if (!hasPlayedRef.current) return
    if (playing) play()
  }, [lang]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (enabled) {
      stop()
      setEnabled(false)
    } else {
      setEnabled(true)
    }
  }

  return { enabled, playing, toggle, play, stop }
}
